import { useMutation } from '@apollo/client';
import type { Policy, UpdatePolicyInput } from '@communities/content-management/domain';
import { UPDATE_COMMUNITY_POLICY, GET_COMMUNITY_POLICIES } from '@communities/content-management/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useCallback, useState } from 'react';

interface UpdateCommunityPolicyResponse {
  updatePolicy: Policy;
}

export function useUpdateCommunityPolicy(communityId?: string) {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [updatePolicyMutation, { loading }] = useMutation<UpdateCommunityPolicyResponse>(UPDATE_COMMUNITY_POLICY, {
    refetchQueries: communityId ? [{ query: GET_COMMUNITY_POLICIES, variables: { communityId } }] : [],
    awaitRefetchQueries: true,
  });

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const updatePolicy = async (communityId: string, policyId: string, input: UpdatePolicyInput): Promise<boolean> => {
    try {
      track('UpdateCommunityPolicyInitiated', {
        communityId,
        policyId,
      });

      setErrors(null);
      await updatePolicyMutation({
        variables: {
          communityId,
          policyId,
          input,
        },
      });

      track('UpdateCommunityPolicySucceeded', {
        communityId,
        policyId,
      });

      return true;
    } catch (error) {
      track('UpdateCommunityPolicyFailed', {
        communityId,
        policyId,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    updatePolicy,
    loading,
    errors,
    resetErrors,
  };
}

export default useUpdateCommunityPolicy;
